import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useFetchPublishedDocumentTypes } from "../../hooks/useDocumentTypes";
import { useDocuments } from "../../hooks/useDocuments";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import DocumentTypeCard from "../../components/documentGeneration/DocumentTypeCard";
import DocumentTypeList from "../../components/documentGeneration/DocumentTypeList";

// Standard components
import Page from "../../../../components/Pages/Page";
import PageLoading from "../../../../components/PageLoading";
import PageError from "../../../../components/PageError";
import PageHeader from "../../../../components/PageHeader";
import LayoutBox from "../../../../components/Cards/LayoutBox";
import SubTitle from "../../../../components/SubTitle";
import Loading from "../../../../components/Loading";

// JoyUI components
import { Button, Box, Card, CardContent, Typography, Grid } from "@mui/joy";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";

const SelectDocumentType: React.FC = () => {
  const { workspace } = useWorkspaceContext();
  const workspace_id = workspace?.id || "1";
  const { documentTypes, loading, error } =
    useFetchPublishedDocumentTypes(workspace_id);
  useDocuments();
  const [isCreating, setIsCreating] = useState(false);
  const _navigate = useNavigate();

  if (!workspace) {
    return <PageLoading>Loading Workspace...</PageLoading>;
  }

  const handleSelect = async (documentTypeId: number) => {
    setIsCreating(true);
    try {
      // Navigate directly to the GenerateDocument view with the document type ID
      // This matches the original meridian repo pattern: /documents/generate/{documentTypeId}
      _navigate(
        `/workspace/${workspace_id}/meridian/documents/generate/${documentTypeId}`,
      );
    } catch (error) {
      console.error("Error navigating to document generation:", error);
      setIsCreating(false);
    }
  };

  if (error) return <PageError>{error}</PageError>;
  if (isCreating) return <PageLoading>Creating Document...</PageLoading>;

  const publishedTypes =
    documentTypes?.filter((dt) => dt.Status === "Published") || [];

  const featuredTypes = publishedTypes.slice(0, 6);
  const additionalTypes = publishedTypes.slice(6);

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">Create New Document</Typography>
        <Button
          component={Link}
          to={`/workspace/${workspace_id}/meridian/documents`}
          variant="outlined"
          startDecorator={<ArrowBackIcon />}
        >
          Back to Documents
        </Button>
      </PageHeader>

      <SubTitle>Select a document type to get started</SubTitle>

      {/* Featured Document Types */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Loading />
            </Box>
          ) : featuredTypes.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography level="body-lg" sx={{ color: "text.secondary" }}>
                No published document types available.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography level="h2" sx={{ mb: 3 }}>
                Featured Document Types
              </Typography>
              <Grid container spacing={3}>
                {featuredTypes.map((docType) => (
                  <Grid xs={12} sm={6} md={4} key={docType.DocumentTypeID}>
                    <DocumentTypeCard
                      documentType={docType}
                      onSelect={handleSelect}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </CardContent>
      </Card>

      {/* Additional Document Types */}
      {!loading && additionalTypes.length > 0 && (
        <LayoutBox>
          <Typography level="h2" sx={{ mb: 2 }}>
            Additional Document Types
          </Typography>
          <DocumentTypeList
            documentTypes={additionalTypes}
            onSelect={handleSelect}
          />
        </LayoutBox>
      )}
    </Page>
  );
};

export default SelectDocumentType;
